import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { getCompanyActivity } from '../services/company_services';
import BoardroomActivityCard from '../components/BoardroomActivityCard';
import { Box, Typography } from '@mui/material';
import LoadingIndicatorLogo from '../components/LoadingIndicatorLogo';
import ACSnackbar from '../components/ACSnackbar';
import BoardMeetingCard from '../components/BoardMeetingsCard';
import { useAccount } from '../contexts/Account';
import TopSection from '../components/Company/TopSection';
import ActivityFilter from '../components/Company/ActivityFilter';
import Treatment from '../components/Treatment';
import { featureFlags } from '../splitSetup';

const normalizeFilterValue = (value) => {
  if (value === undefined || value === null || value === '') return null;
  if (Array.isArray(value)) return value;
  return value;
};

const filtersEqual = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((item, index) => item === b[index]);
  }
  if (Array.isArray(a) || Array.isArray(b)) return false;
  if (a === 'all' && (b === 'all' || b == null)) return true;
  if (b === 'all' && a == null) return true;
  return a === b;
};

const Activities = function Activities({ defaultFilter = null }) {
  const location = useLocation();
  const history = useHistory();
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false);
  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingActivities, setLoadingActivities] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [activityFilter, setActivityFilter] = useState(() => {
    const parsed = queryString.parse(location.search);
    if (Object.prototype.hasOwnProperty.call(parsed, 'filter')) {
      return normalizeFilterValue(parsed.filter);
    }
    return normalizeFilterValue(defaultFilter);
  });

  const containerRef = useRef(null);

  const hasMoreRef = useRef(hasMore);
  const loadingRef = useRef(isLoadingActivities);
  const filterRef = useRef(activityFilter);
  const offsetRef = useRef(offset);
  const hasAppliedInitialUrlFilter = useRef(false);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    loadingRef.current = isLoadingActivities;
  }, [isLoadingActivities]);
  useEffect(() => {
    filterRef.current = activityFilter;
  }, [activityFilter]);
  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  const { accountDetails } = useAccount();
  const company = accountDetails || {};
  const limit = 10;

  const fetchItems = useCallback(
    async (nextOffset = offsetRef.current) => {
      if (loadingRef.current || (!hasMoreRef.current && nextOffset > 0)) return;
      setLoadingActivities(true);
      try {
        const f = filterRef.current;
        const filterType =
          f == null || f === 'all' ? null : Array.isArray(f) ? f : [f];

        const response = await getCompanyActivity({
          companyId: company.id,
          limit,
          offset: nextOffset,
          filterType:
            filterType[0] === 'breakthroughs' ? ['advisorIdea'] : filterType,
          onlyBreakthroughs: filterType[0] === 'breakthroughs' ? true : false,
        });

        const page = response?.data?.getCompanyActivity ?? [];

        setItems((prev) => (nextOffset === 0 ? page : [...prev, ...page]));
        setHasMore(page.length > 0);
        setOffset(nextOffset + limit);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingActivities(false);
      }
    },
    [company.id, limit]
  );

  const syncUrlWithFilter = useCallback(
    (val) => {
      const currentParams = queryString.parse(history.location.search);
      const nextParams = { ...currentParams };

      if (
        val == null ||
        (Array.isArray(val) && val.length === 0) ||
        val === 'all'
      ) {
        delete nextParams.filter;
      } else {
        nextParams.filter = val;
      }

      const nextSearch = queryString.stringify(nextParams);
      const formatted = nextSearch ? `?${nextSearch}` : '';

      if (formatted !== history.location.search) {
        history.replace({
          pathname: history.location.pathname,
          search: formatted,
        });
      }
    },
    [history]
  );

  const handleFilterChange = useCallback(
    (val, { syncUrl = true } = {}) => {
      const normalized = normalizeFilterValue(val);
      if (filtersEqual(normalized, filterRef.current)) {
        if (syncUrl) {
          syncUrlWithFilter(normalized);
        }
        return;
      }

      setActivityFilter(normalized);
      setItems([]);
      setHasMore(true);
      setOffset(0);
      offsetRef.current = 0;
      filterRef.current = normalized;

      if (containerRef.current) {
        containerRef.current.scrollTo({ top: 0, behavior: 'auto' });
      }

      if (syncUrl) {
        syncUrlWithFilter(normalized);
      }

      fetchItems(0);
    },
    [fetchItems, syncUrlWithFilter]
  );

  useEffect(() => {
    fetchItems(0);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const { scrollTop, clientHeight, scrollHeight } = el;
        if (
          scrollTop + clientHeight >= scrollHeight - 120 &&
          hasMoreRef.current &&
          !loadingRef.current
        ) {
          fetchItems(offsetRef.current);
        }
      });
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [fetchItems]);

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const hasFilterParam = Object.prototype.hasOwnProperty.call(
      parsed,
      'filter'
    );
    const nextFilter = normalizeFilterValue(
      hasFilterParam ? parsed.filter : null
    );

    if (!hasAppliedInitialUrlFilter.current) {
      hasAppliedInitialUrlFilter.current = true;
      if (!hasFilterParam && defaultFilter != null) {
        return;
      }
    }

    if (!filtersEqual(nextFilter, filterRef.current)) {
      handleFilterChange(nextFilter, { syncUrl: false });
    }
  }, [location.search, handleFilterChange, defaultFilter]);

  return (
    <Box
      ref={containerRef}
      sx={{
        height: 'calc(100vh - 50px)',
        overflowY: 'auto',
        backgroundColor: '#f4f8ff',
        width: '100%',
        p: { xxs: '16px', xs: '32px' },
      }}
    >
      <ACSnackbar
        onClose={() => setIsSnackbarVisible(false)}
        open={isSnackbarVisible}
        severity="warning"
        text="This company has not scheduled any board Activities yet."
      />
      <TopSection />

      <Typography
        sx={{
          mt: '20px',
          color: '#001030',
          fontWeight: 600,
          fontSize: '20px',
          lineHeight: '130%',
        }}
      >
        Boardroom Activity
      </Typography>
      <Typography
        sx={{
          mt: '10px',
          mb: '20px',
          color: '#4D586F',
          fontWeight: 400,
          fontSize: '14px',
          fontStyle: 'italic',
          lineHeight: '150%',
        }}
      >
        View updates, ideas, introductions and more from the{' '}
        {company.displayName} Advisory Board
      </Typography>

      <Treatment
        splitNames={featureFlags.breakthroughs}
        evaluatedComponent={
          <ActivityFilter
            value={activityFilter}
            onChange={handleFilterChange}
            size="small"
            fullWidth
            sx={{ mb: '8px' }}
            breakthroughsEnabled
          />
        }
        conditionFailedComponent={
          <ActivityFilter
            value={activityFilter}
            onChange={handleFilterChange}
            size="small"
            fullWidth
            sx={{ mb: '8px' }}
          />
        }
      />

      <Treatment
        splitNames={featureFlags.breakthroughs}
        evaluatedComponent={items.map((item) =>
          item.activityType === 'boardMeetingCreated' ? (
            <Box sx={{ mb: '30px' }} key={item.id}>
              <BoardMeetingCard
                boardMeeting={item.boardMeeting}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                company={company}
              />
            </Box>
          ) : (
            <BoardroomActivityCard
              key={item.id}
              activity={item}
              company={company}
              breakthroughsEnabled
            />
          )
        )}
        conditionFailedComponent={items.map((item) =>
          item.activityType === 'boardMeetingCreated' ? (
            <Box sx={{ mb: '30px' }} key={item.id}>
              <BoardMeetingCard
                boardMeeting={item.boardMeeting}
                setIsLoading={setIsLoading}
                isLoading={isLoading}
                company={company}
              />
            </Box>
          ) : (
            <BoardroomActivityCard
              key={item.id}
              activity={item}
              company={company}
            />
          )
        )}
      />

      {isLoading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
          }}
        >
          <LoadingIndicatorLogo size={200} iconFontSize={93} iconRight={105} />
        </Box>
      )}

      {isLoadingActivities && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '180px',
          }}
        >
          <LoadingIndicatorLogo size={100} iconFontSize={40} iconRight={46} />
        </Box>
      )}
    </Box>
  );
};

export default Activities;
